import dayjs from "dayjs";
import { calculateGroundTime } from "../../../../utils/timeUtils";
import { getAirportMinGroundTime } from "../../../../utils/minGroundTimeUtils";

/**
 * 航班验证服务 - 处理航班约束验证相关逻辑
 */
class ValidationService {
    /**
     * 检查航班违反的约束
     * @param {string} flightId - 航班ID
     * @param {Array} validationResults - 验证结果数组
     * @returns {Array} 航班违反的全部约束结果
     */
    static getFlightViolations(flightId, validationResults) {
        if (!validationResults || validationResults.length === 0) return [];
        return validationResults.filter((result) => String(result.flightId) === String(flightId));
    }

    /**
     * 检查航班对违反的约束（如过站时间等）
     * @param {string} flightId - 航班ID
     * @param {Array} validationResults - 验证结果数组
     * @returns {Array|null} 航班对违反的约束结果数组
     */
    static getFlightPairViolations(flightId, validationResults) {
        if (!validationResults || validationResults.length === 0) return null;
        return validationResults.filter((result) => result.flightIds && result.flightIds.includes(flightId));
    }

    /**
     * 检查地面时间约束
     *
     * 与 ConstraintsService.validateMinGroundTime 同一口径：最小过站只认到达站在
     * 机场主数据里的明确配置（含 0 分钟）。航班上存着的 min_ground_time_minutes
     * 不参与判断；机场没配时返回 null，缺配置由 validateMinGroundTime 单独报出。
     *
     * @param {Object} flight - 当前航班
     * @param {Object} nextFlight - 下一个航班
     * @param {Object} airportConstraints - 机场约束对象
     * @returns {Object|null} 违反的约束结果
     */
    static checkGroundTimeViolation(flight, nextFlight, airportConstraints = {}) {
        if (!flight || !nextFlight || flight.aircraft_reg !== nextFlight.aircraft_reg) {
            return null;
        }

        // 检查是否有地面时间
        if (!flight.sta || !nextFlight.std) {
            return null;
        }

        // 计算实际地面时间
        const actualGroundTime = calculateGroundTime(flight.sta, nextFlight.std);
        if (actualGroundTime === null) {
            return null;
        }

        const minGroundTime = getAirportMinGroundTime(airportConstraints, flight.arrival_airport);
        if (minGroundTime === null) {
            return null;
        }

        if (actualGroundTime < minGroundTime) {
            return {
                type: "ground_time",
                flightIds: [flight.id, nextFlight.id],
                message: `地面时间不足 (要求: ${minGroundTime}分钟, 实际: ${actualGroundTime}分钟)`,
                severity: "warning",
                actualTime: actualGroundTime,
                requiredTime: minGroundTime,
                violationType: "ground_time",
            };
        }

        return null;
    }

    /**
     * 检查航班连续性约束
     * @param {Object} flight - 当前航班
     * @param {Object} nextFlight - 下一个航班
     * @returns {Object|null} 违反的约束结果
     */
    static checkContinuityViolation(flight, nextFlight) {
        if (!flight || !nextFlight || flight.aircraft_reg !== nextFlight.aircraft_reg) {
            return null;
        }

        // 检查航班连续性 - 上一个航班的到达机场应该是下一个航班的出发机场
        if (flight.arrival_airport !== nextFlight.departure_airport) {
            return {
                flightId: nextFlight.id,
                violationType: "flight_continuity",
                messages: [`航班连续性违反: ${flight.flight_number} 到达 ${flight.arrival_airport}, 但 ${nextFlight.flight_number} 从 ${nextFlight.departure_airport} 起飞`],
                severity: "warning",
            };
        }

        return null;
    }

    /**
     * 检查时间连续性约束
     * @param {Object} flight - 当前航班
     * @param {Object} nextFlight - 下一个航班
     * @returns {Object|null} 违反的约束结果
     */
    static checkTimeSequenceViolation(flight, nextFlight) {
        if (!flight || !nextFlight) {
            return null;
        }

        // 检查时间顺序 - 下一个航班起飞时间需要在上一个航班到达时间之后
        if (flight.sta && nextFlight.std) {
            const staTime = dayjs.utc(flight.sta);
            const stdTime = dayjs.utc(nextFlight.std);

            if (stdTime.isBefore(staTime)) {
                return {
                    flightIds: [flight.id, nextFlight.id],
                    violationType: "time_sequence",
                    message: `时间顺序违反: ${flight.flight_number} 到达时间 (${staTime.format("YYYY-MM-DD HH:mm")}) 晚于 ${nextFlight.flight_number} 起飞时间 (${stdTime.format("YYYY-MM-DD HH:mm")})`,
                    severity: "error",
                };
            }
        }

        return null;
    }

    /**
     * 合并航班验证结果
     * @param {Array} validations - 验证结果数组
     * @returns {Array} 合并和过滤后的验证结果
     */
    static mergeValidationResults(validations) {
        // 过滤无效结果
        const filteredResults = validations.filter((result) => result != null);

        // 按ID分组
        const groupedByFlightId = {};
        filteredResults.forEach((result) => {
            const flightId = result.flightIds ? result.flightIds.join("-") : result.flightId;
            if (!groupedByFlightId[flightId]) {
                groupedByFlightId[flightId] = [];
            }
            groupedByFlightId[flightId].push(result);
        });

        // 合并相同航班的结果
        return Object.values(groupedByFlightId).map((group) => {
            if (group.length === 1) {
                return group[0];
            }

            // 合并多个结果
            const merged = { ...group[0] };
            merged.messages = [];
            merged.violationTypes = [];

            // 优先级: error > warning > notice
            let highestSeverity = "notice";

            group.forEach((item) => {
                // 添加消息
                if (item.messages && item.messages.length > 0) {
                    merged.messages.push(...item.messages);
                } else if (item.message) {
                    merged.messages.push(item.message);
                }

                // 添加违反类型
                if (item.violationType) {
                    merged.violationTypes.push(item.violationType);
                }

                // 更新严重程度
                if (item.severity === "error" || (item.severity === "warning" && highestSeverity === "notice")) {
                    highestSeverity = item.severity;
                }
            });

            merged.severity = highestSeverity;
            return merged;
        });
    }
}

export default ValidationService;
